import { useEffect, useState } from 'react';
import { BellAlertIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/stores/authStore';
import { pushState, turnOnPush } from '@/services/pushSubscription';

/**
 * "Get check-in reminders and visitor alerts on this device" (Employee
 * rebuild C12, the prototype's #pushbar). App-wide, above the page.
 *
 * Shown only when turning push on would actually do something: the browser
 * can, the deployment has a VAPID key, the person has not blocked it, and
 * this device is not already subscribed. "Not now" hides it for
 * REASK_AFTER_DAYS — a per-viewer convenience, so localStorage, keyed by
 * person so a shared device asks each of them.
 */
export const REASK_AFTER_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

export function dismissKey(userId: string): string {
  return `spaciora-push-prompt-dismissed:${userId}`;
}

export function dismissedRecently(userId: string, now = Date.now()): boolean {
  try {
    const at = Number(localStorage.getItem(dismissKey(userId)));
    return Number.isFinite(at) && at > 0 && now - at < REASK_AFTER_DAYS * DAY_MS;
  } catch {
    return false;
  }
}

export default function PushPromptBar() {
  const userId = useAuthStore((s) => s.user?.id);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!userId || dismissedRecently(userId)) {
      setShow(false);
      return undefined;
    }
    pushState()
      .then((state) => { if (!cancelled) setShow(state === 'off'); })
      .catch(() => { if (!cancelled) setShow(false); });
    return () => { cancelled = true; };
  }, [userId]);

  if (!show || !userId) return null;

  const notNow = () => {
    try { localStorage.setItem(dismissKey(userId), String(Date.now())); } catch { /* blocked */ }
    setShow(false);
  };

  const turnOn = async () => {
    setBusy(true);
    try {
      const state = await turnOnPush();
      if (state === 'subscribed') {
        toast.success('Notifications turned on for this device');
        setShow(false);
      } else if (state === 'denied') {
        toast.error('Notifications are blocked for this site in your browser settings.');
        setShow(false);
      } else {
        // Closed the browser's prompt without answering: ask again later.
        notNow();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not turn notifications on');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="region"
      aria-label="Turn on notifications"
      data-testid="push-prompt-bar"
      className="flex flex-wrap items-center gap-3 border-b border-brand-200 bg-brand-50 px-4 py-2 text-sm text-content lg:px-6"
    >
      <BellAlertIcon className="h-5 w-5 shrink-0 text-brand-700" aria-hidden="true" />
      <p className="min-w-0 flex-1">
        Get check-in reminders and visitor alerts on this device, even when Spaciora is closed.
      </p>
      <div className="flex shrink-0 items-center gap-2">
        <button type="button" className="btn-primary px-3 py-1.5 text-xs" onClick={turnOn} disabled={busy}>
          {busy ? 'Turning on…' : 'Turn on'}
        </button>
        <button type="button" className="btn-secondary px-3 py-1.5 text-xs" onClick={notNow} disabled={busy}>
          Not now
        </button>
        <button
          type="button"
          onClick={notNow}
          aria-label="Dismiss"
          className="rounded p-1 text-content-muted hover:bg-brand-100"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
